/**
 * ТЗ 0124 — хаб «Опросы» (`/surveys`): вкладки и режимы «Результатов».
 *
 * Всё состояние хаба — в адресе: `?tab=` (вкладка) и `?mode=` (режим
 * «Результатов»). Экраны внутри вкладок пишут свои параметры сами
 * (`useInsightsDeepLink`, `useAnalyticsDeepLink`) и обязаны сохранять эти
 * два — иначе первая же правка фильтра выкидывала бы на вкладку по умолчанию.
 */

export const SURVEY_HUB_TABS = [
  'results',
  'templates',
  'analytics',
  'teachers',
] as const;
export type SurveyHubTab = typeof SURVEY_HUB_TABS[number];

export const SURVEY_RESULT_MODES = [
  'course',
  'group',
  'student',
  'all',
] as const;
export type SurveyResultMode = typeof SURVEY_RESULT_MODES[number];

/** Параметры хаба, которые переживают перезапись адреса экраном вкладки. */
export const SURVEY_HUB_PARAMS = ['tab', 'mode'] as const;

export const isSurveyHubTab = (value: unknown): value is SurveyHubTab =>
  (SURVEY_HUB_TABS as readonly unknown[]).includes(value);

export const isSurveyResultMode = (value: unknown): value is SurveyResultMode =>
  (SURVEY_RESULT_MODES as readonly unknown[]).includes(value);

/** Переносит `tab`/`mode` из текущего адреса в собираемый. */
export const keepSurveyHubParams = (
  from: URLSearchParams,
  to: URLSearchParams,
): URLSearchParams => {
  SURVEY_HUB_PARAMS.forEach((key) => {
    const value = from.get(key);
    if (value && !to.has(key)) to.set(key, value);
  });
  return to;
};

export interface ISurveyHubAccess {
  canManageSurveys: boolean;
  canReadInsights: boolean;
}

/**
 * Вкладки, видимые роли. Шаблоны — только владельцам опросов
 * (`SURVEY_MANAGER_ROLES`); результаты, аналитика и преподаватели — всему
 * кругу чтения (`SURVEY_INSIGHT_READ_ROLES`, с HR).
 */
export const surveyHubTabsFor = ({
  canManageSurveys,
  canReadInsights,
}: ISurveyHubAccess): SurveyHubTab[] =>
  SURVEY_HUB_TABS.filter((tab) =>
    tab === 'templates' ? canManageSurveys : canReadInsights,
  );

/** Старые адреса разделов → вкладка хаба (они остаются и ведут сюда). */
export const LEGACY_SURVEY_TAB: Readonly<Record<string, SurveyHubTab>> = {
  insights: 'results',
  analytics: 'analytics',
  teachers: 'teachers',
};

/**
 * Адрес хаба для старого маршрута: query сохраняется. У аналитики своя
 * вкладка «Комментарии» раньше жила в `?tab=comments` — теперь `tab` занят
 * хабом, и она переехала в `?view=comments`.
 */
export const legacySurveyHubSearch = (
  tab: SurveyHubTab,
  search: string,
): string => {
  const params = new URLSearchParams(search);
  const legacyTab = params.get('tab');
  if (tab === 'analytics' && legacyTab === 'comments') {
    params.set('view', 'comments');
  }
  params.set('tab', tab);
  return `?${params.toString()}`;
};
